import { Fragment, type Node as PMNode, type Schema } from '@tiptap/pm/model';

import { numberHeadings, type THeadingState, type TTreeHeading } from '../../tiptap/nodes/heading/headingNumbering';
import { SECTION_NODE_NAME } from '../../tiptap/nodes/section/constants';
import type { TSectionPiece } from '../constants';
import {
  buildContainedIdIndex,
  indexOfId,
  joinElements,
  nodeCount,
  splitElements,
  withoutComment,
  titleTextOf,
  withTitleElement,
  type TElement,
} from './elements';
import { numberingSeed, renumberElements, type TNumberingSeed } from './numbering';
import { computeParts, partIndexOfElement, type TPart } from './parts';
import { parseWholeDoc } from './wholeDocument';
import { trace as traceEvent } from '../trace';

const traceCommit = (data: Record<string, unknown>) => traceEvent('commit:splice', data);

export type TModelSnapshot = {
  elementsAbove: readonly TElement[];
  elementsBelow: readonly TElement[];
};

export type TDocumentModel = ReturnType<typeof createDocumentModel>;

type TOptions = { schema: Schema; html: string };

export const createDocumentModel = ({ schema, html }: TOptions) => {
  // A document with no title node in its schema (reusable content, the mock sheet) must not be given
  // one: the element would round-trip into the saved html as an element the document never had.
  const supportsTitle = Boolean(schema.nodes.title);
  let elements: TElement[] = supportsTitle ? withTitleElement(splitElements(html)) : splitElements(html);
  let parts: TPart[] = computeParts(elements);
  let active = 0;
  // The exact range the editor holds. A commit may re-cut `parts`, but it does not change what is
  // already in the editor; keeping that range prevents the next commit from splicing over a new one.
  let liveRange: TPart = parts[0] ?? { startElement: 0, endElement: elements.length };
  // Where the two static halves meet while they draw the whole document, null the rest of the time.
  // Held for the whole swap so no element changes half while the editor is hidden.
  let staticHalvesSplit: number | null = null;
  /** Each element's cleaned html, keyed by the element string itself - see `saveHtml`. */
  let cleanCache = new Map<string, string>();

  const listeners = new Set<() => void>();
  let snapshot: TModelSnapshot;

  const range = (): TPart => liveRange;

  /**
   * Rewriting the elements changes how many of them a part holds, which shifts every index below
   * it - so the part the editor is holding has to be found again, by where its range now starts.
   */
  const recutAroundLive = () => {
    parts = computeParts(elements);
    const found = parts.findIndex(
      (part) => liveRange.startElement >= part.startElement && liveRange.startElement < part.endElement
    );
    active = found >= 0 ? found : Math.min(active, Math.max(parts.length - 1, 0));
  };

  const buildSnapshot = (): TModelSnapshot => {
    const { startElement: start, endElement: end } = range();
    // Mid-swap the outgoing part stays visible as static content while the editor rebuilds
    // hidden, so the page never loses its height.
    return {
      elementsAbove: elements.slice(0, staticHalvesSplit ?? start),
      elementsBelow: elements.slice(staticHalvesSplit ?? end),
    };
  };
  snapshot = buildSnapshot();

  const notify = () => {
    snapshot = buildSnapshot();
    listeners.forEach((listener) => listener());
  };

  // Keyed by array identity: a commit replaces the element list, so an unchanged reference proves the parsed doc is current.
  let docCache: { elements: readonly TElement[]; doc: PMNode } | null = null;
  const wholeDoc = (): PMNode => {
    if (docCache?.elements === elements) return docCache.doc;
    const doc = parseWholeDoc(schema, elements);
    docCache = { elements, doc };
    return doc;
  };

  let sectionCache: { elements: readonly TElement[]; has: boolean } | null = null;
  let containedIds: { elements: readonly TElement[]; index: Map<string, number> } | null = null;
  const hasSectionNode = (): boolean => {
    // Read off the elements, not the parsed doc: the model's doc is flat, so no section node is in it.
    if (sectionCache?.elements !== elements) {
      sectionCache = { elements, has: elements.some((element) => Boolean(element.section)) };
    }
    return sectionCache.has;
  };

  /**
   * The editor's nodes that stand for elements. A section is not one: the model splits it into its
   * children and records the section on each, so the editor holds the wrapper only to lay the page
   * out. Asked of every child, because a user can insert a section beside other content, and the
   * type is asked of the node rather than guessed from the count - the user can unwrap it too.
   */
  const liveElementNodes = (liveDoc: PMNode): PMNode[] => {
    const nodes: PMNode[] = [];
    liveDoc.forEach((child) => {
      if (child.type.name === SECTION_NODE_NAME) child.forEach((inner) => nodes.push(inner));
      else nodes.push(child);
    });
    return nodes;
  };

  // `copy`, not `create`: a transient mismatch between the model and the editor must not throw.
  const docWithLivePart = (liveDoc: PMNode): PMNode => {
    const doc = wholeDoc();
    const { startElement: start, endElement: end } = range();
    const children: PMNode[] = [];
    for (let i = 0; i < start && i < doc.childCount; i++) children.push(doc.child(i));
    liveElementNodes(liveDoc).forEach((child) => children.push(child));
    for (let i = end; i < doc.childCount; i++) children.push(doc.child(i));
    return doc.copy(Fragment.fromArray(children));
  };

  const elementOfContainedId = (id: string): number => {
    if (containedIds?.elements !== elements) containedIds = { elements, index: buildContainedIdIndex(elements) };
    return containedIds.index.get(id) ?? -1;
  };

  const elementOfId = (id: string): number => {
    const topLevel = indexOfId(elements, id);
    return topLevel >= 0 ? topLevel : elementOfContainedId(id);
  };

  /** Position of an element boundary in the whole doc, in the doc's content coordinates. */
  const positionOfElement = (doc: PMNode, elementIndex: number): number => {
    let position = 0;
    for (let i = 0; i < elementIndex && i < doc.childCount; i++) position += doc.child(i).nodeSize;
    return position;
  };

  return {
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,

    elements: (): readonly TElement[] => elements,
    // The one lookup for "which of the editor's nodes stand for elements" - a caller that counts the
    // doc's own children instead sees 1 for a section part, whose elements are inside the section.
    liveElementNodes,
    parts: (): readonly TPart[] => parts,
    activePart: () => active,
    // Asked of the range, never the part index: a commit re-cuts the parts and the index moves with them.
    holdsStart: () => range().startElement === 0,
    holdsEnd: () => range().endElement === elements.length,
    wholeDoc: (liveDoc?: PMNode): PMNode => (liveDoc ? docWithLivePart(liveDoc) : wholeDoc()),

    partHtml: (index: number): string => {
      const part = parts[Math.max(0, Math.min(index, parts.length - 1))];
      return part ? joinElements(elements.slice(part.startElement, part.endElement)) : '';
    },

    commitFromEditor: (editorHtml: string, liveDoc?: PMNode): boolean => {
      const { startElement: start, endElement: end } = range();
      /**
       * The section comes from the model: a section is in `UNIQUE_ID_NODE_TYPES`, so the editor can
       * hand one back under a reassigned id, which would split one section in two at the next join.
       * An editor that returned no section at all is an unwrap, and that is the user's edit to keep.
       */
      const liveSection = elements[start]?.section;
      const read = splitElements(editorHtml).map((element) =>
        liveSection && element.section ? { ...element, section: liveSection } : element
      );

      // Nothing came back changed - browsing, not editing: renumbering runs inside the click that asked for the swap.
      const unchanged = (element: TElement, i: number) =>
        element.html === elements[start + i].html && element.section === elements[start + i].section;
      if (read.length === end - start && read.every(unchanged)) return false;

      const next = [...elements.slice(0, start), ...read, ...elements.slice(end)];
      const liveNodes = liveDoc ? liveElementNodes(liveDoc).length : null;
      const canSplice = Boolean(liveDoc) && docCache?.elements === elements && liveNodes === read.length;
      traceCommit({
        canSplice,
        hasLiveDoc: Boolean(liveDoc),
        cached: docCache?.elements === elements,
        liveNodes,
        readElements: read.length,
      });
      const prepared = canSplice && liveDoc ? docWithLivePart(liveDoc) : undefined;

      const renumbered = renumberElements(schema, next, prepared);
      elements = renumbered.elements;
      docCache = { elements, doc: renumbered.doc };

      liveRange = { startElement: start, endElement: start + read.length };
      recutAroundLive();
      notify();
      return true;
    },

    setActivePart: (index: number): boolean => {
      const next = Math.max(0, Math.min(index, parts.length - 1));
      const nextRange = parts[next] ?? { startElement: 0, endElement: elements.length };
      if (
        next === active &&
        liveRange.startElement === nextRange.startElement &&
        liveRange.endElement === nextRange.endElement
      )
        return false;
      active = next;
      liveRange = nextRange;
      notify();
      return true;
    },

    /** Per-part sizes, for diagnosing a slow swap. Measurement only. */
    partShape: () =>
      parts.map((part, index) => {
        const slice = elements.slice(part.startElement, part.endElement);
        const html = slice.map((element) => element.html);
        const joined = html.join('');
        return {
          part: index,
          elements: html.length,
          chars: joined.length,
          biggestElement: html.reduce((most, one) => Math.max(most, one.length), 0),
          images: (joined.match(/<img/g) ?? []).length,
          nodeViews: (joined.match(/<(reusable-block|inline-block|ref|caption-node|literature-citation)/g) ?? [])
            .length,
          tableCells: (joined.match(/<t[dh][\s>]/g) ?? []).length,
          // The same count computeParts budgets a part on.
          tags: slice.reduce((total, element) => total + nodeCount(element), 0),
        };
      }),

    partOfId: (id: string): number => {
      const index = elementOfId(id);
      return index < 0 ? -1 : partIndexOfElement(parts, index);
    },

    // Elements with no id of their own - a bare page break, a comment selection - can only be found by position.
    partOfElementIndex: (index: number): number =>
      index < 0 || index >= elements.length ? -1 : partIndexOfElement(parts, index),

    /** Whether a target is already in the editor, even when a re-cut made its numeric part ambiguous. */
    isElementInActiveRange: (id: string, elementIndex?: number): boolean => {
      const index = elementIndex ?? elementOfId(id);
      const { startElement, endElement } = range();
      return index >= startElement && index < endElement;
    },

    // BEFORE, not anywhere: `CustomUniqueId`'s rule is that the first occurrence keeps its id.
    hasIdBeforeActivePart: (id: string): boolean => {
      const index = elementOfContainedId(id);
      return index >= 0 && index < range().startElement;
    },

    /**
     * Take one comment's highlight out of the elements the EDITOR DOES NOT HOLD.
     *
     * Deleting a comment walks the editor's document, which is one part - so a comment anchored
     * anywhere else lost its card and kept its highlight, with nothing left that would ever remove it.
     * The editor still does its own part; this does the rest, without loading anything, because a
     * document jumping to some other part on a delete would read as a bug of its own.
     */
    removeCommentOutsideLivePart: (commentId: string): boolean => {
      const { startElement, endElement } = range();
      const next: TElement[] = [];
      let addedBeforeLive = 0;
      let changed = false;
      elements.forEach((element, index) => {
        const live = index >= startElement && index < endElement;
        if (live || !element.html.includes(commentId)) {
          next.push(element);
          return;
        }
        const replacement = withoutComment(element, commentId);
        changed = true;
        // Unwrapping a comment that wrapped whole blocks turns one element into several, and every
        // index below it moves - including the range the editor is holding.
        if (index < startElement) addedBeforeLive += replacement.length - 1;
        next.push(...replacement);
      });
      if (!changed) return false;

      elements = next;
      docCache = null;
      containedIds = null;
      liveRange = {
        startElement: liveRange.startElement + addedBeforeLive,
        endElement: liveRange.endElement + addedBeforeLive,
      };
      recutAroundLive();
      notify();
      return true;
    },

    /**
     * Split where the INCOMING part starts, so every element is already in the half it ends up in.
     * One that changed half would be drawn against different neighbours, and a margin that collapses
     * against one and not the other moves it.
     */
    drawWholeDocumentStatically: (targetPart: number) => {
      const incoming = parts[Math.max(0, Math.min(targetPart, parts.length - 1))];
      staticHalvesSplit = incoming?.startElement ?? 0;
      notify();
    },

    drawAroundLivePart: () => {
      staticHalvesSplit = null;
      notify();
    },

    // Cleaning per element is equivalent: nothing `cleanForSave` removes spans two top-level elements.
    saveHtml: (clean?: (html: string) => string): string => {
      if (!clean) return joinElements(elements);
      const next = new Map<string, string>();
      let cleaned = 0;
      const cleanedElements = elements.map((element) => {
        // `next` first: a document that repeats a unit holds the same string many times, and it only
        // needs cleaning once per save even when the cache has just been invalidated.
        const known = next.get(element.html) ?? cleanCache.get(element.html);
        const result = known ?? clean(element.html);
        if (known === undefined) cleaned++;
        // Every element goes into `next`, hit or miss - it REPLACES the cache, so a hit that is not
        // carried over would be re-cleaned on the save after this one, forever.
        next.set(element.html, result);
        return { ...element, html: result };
      });
      // Rebuilt rather than added to, so elements the document no longer has do not accumulate.
      cleanCache = next;
      traceEvent('save:clean', { elements: elements.length, cleaned });
      return joinElements(cleanedElements);
    },

    liveSectionPiece: (): TSectionPiece | null => {
      const { startElement, endElement } = range();
      const section = elements[startElement]?.section;
      if (!section) return null;
      const first = elements[startElement - 1]?.section !== section;
      const last = elements[endElement]?.section !== section;
      return first && last ? 'only' : first ? 'first' : last ? 'last' : 'middle';
    },

    hasSectionNode,

    // Element 0, never `editor.state.doc.firstChild` - a part that is not the first has no title node.
    title: (): string => titleTextOf(elements),

    numberingSeed: (): TNumberingSeed => {
      const doc = wholeDoc();
      const { startElement: start, endElement: end } = range();
      return numberingSeed(doc, positionOfElement(doc, start), positionOfElement(doc, end));
    },

    // `liveDoc` folds in uncommitted edits - without it a heading just typed is missing until the next commit.
    headingTree: (headingStart: number, liveDoc?: PMNode): TTreeHeading[] =>
      numberHeadings(liveDoc ? docWithLivePart(liveDoc) : wholeDoc(), headingStart).tree,

    // The plugin continues the document's numbering instead of restarting at 1.
    headingSeed: (headingStart: number): THeadingState => {
      const doc = wholeDoc();
      return numberHeadings(doc.cut(0, positionOfElement(doc, range().startElement)), headingStart).state;
    },

    dispose: () => {
      listeners.clear();
      docCache = null;
    },
  };
};
